import { Typography } from '@mui/material'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import {
  BookmarkIcon,
  DeleteIcon,
  FileCopyIcon,
  ImportIcon,
} from '~components/icons'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { Tooltip } from '~components/tooltip'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { useAddBookmark } from '~hooks/queries/documents/use-add-bookmark'
import {
  GetDocumentRes,
  useGetDocument,
} from '~hooks/queries/documents/use-get-document'
import { IDocument } from '~hooks/queries/documents/use-get-documents'
import { useRemoveBookmark } from '~hooks/queries/documents/use-remove-bookmark'
import { useDateFormat } from '~hooks/use-date-format'
import {
  BookmarkWrapper,
  ContentWrapper,
  DocumentButtons,
  DocumentInfo,
  DocumentSeparator,
  DocumentWrapper,
  FileIconWrapper,
  StyledBookmarkFilledIcon,
} from '~pages/pages-behind-login/documents/documents.styles'
import { getFileIcon } from '~pages/pages-behind-login/documents/file-type/file-type-icon-helper'
import DeletePoolDocumentModal from './components/delete-pool-document-modal'

export function Document({
  singleDocument,
  onDocumentClick,
}: {
  singleDocument: IDocument | GetDocumentRes
  onDocumentClick?: () => void
}) {
  const [copiedRecently, setCopiedRecently] = useState(false)
  const { mutate: removeBookmark, status: removeBookmarkStatus } =
    useRemoveBookmark()
  const { mutate: addBookmark, status: addBookmarkStatus } = useAddBookmark()
  const { setStatus: setAnimatedAlertStatus } = useAnimatedAlert()
  const [bookmarkState, setBookmarkState] = useState<undefined | boolean>(
    singleDocument.userBookmark,
  )
  const dateFormat = useDateFormat()
  const canManagePoolDocuments = useHasScope(ExactScopeName.poolAdmin)

  useEffect(() => {
    if (addBookmarkStatus === 'error' || removeBookmarkStatus === 'error') {
      setAnimatedAlertStatus('Updating bookmark failed.', undefined, 'error')
    }
    if (addBookmarkStatus === 'success' || removeBookmarkStatus === 'success') {
      setAnimatedAlertStatus('Updating bookmark success.', undefined, 'success')
    }
  }, [addBookmarkStatus, removeBookmarkStatus])

  const { refetch: fetchDownloadLink } = useGetDocument({
    documentId: singleDocument?._id,
    attachment: true,
    disabled: true,
  })

  const downloadDocument = async () => {
    const { data } = await fetchDownloadLink()
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const copyDocumentLink = async () => {
    const documentUrl = new URL(window.location.href)
    documentUrl.searchParams.set('documentId', singleDocument._id)
    navigator.clipboard.writeText(documentUrl.toString())
    setCopiedRecently(true)
    await new Promise((r) => setTimeout(r, 1500))
    setCopiedRecently(false)
  }

  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false)

  const documentExtension = getFileExtension(singleDocument.name)

  return (
    <>
      <DeletePoolDocumentModal
        confirmDeleteDialogOpen={confirmDeleteDialogOpen}
        setConfirmDeleteDialogOpen={setConfirmDeleteDialogOpen}
        title={singleDocument.title || 'No Document Title'}
        id={singleDocument._id}
      />
      <DocumentWrapper
        key={singleDocument._id}
        onClickHandler={() => onDocumentClick?.()}
      >
        <ContentWrapper>
          <FileIconWrapper>{getFileIcon(documentExtension)}</FileIconWrapper>
          <DocumentInfo
            publishDate={
              singleDocument?.uploadedDate
                ? format(new Date(singleDocument?.uploadedDate), dateFormat)
                : 'Invalid Date'
            }
            title={singleDocument.title || 'No Document Title'}
            category={singleDocument.category}
            vesselName={singleDocument.vesselName}
          />
        </ContentWrapper>
        <DocumentSeparator />
        <DocumentButtons>
          {copiedRecently ? (
            <Typography variant="paragraph1Secondary">Link copied!</Typography>
          ) : (
            <Tooltip title="Copy link to document">
              <FileCopyIcon
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  copyDocumentLink()
                }}
              />
            </Tooltip>
          )}
          {canManagePoolDocuments && (
            <Tooltip title="Delete document">
              <DeleteIcon
                color="red"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  setConfirmDeleteDialogOpen(true)
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Download document">
            <ImportIcon
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                downloadDocument()
              }}
            />
          </Tooltip>
          <Tooltip title="Bookmark document">
            <BookmarkWrapper
              className="tour-step-dashboard-documents-bookmark"
              onClick={(e) => {
                e.stopPropagation()
                setBookmarkState(!bookmarkState)

                if (singleDocument.userBookmark) {
                  trackBookmarkButtonClick(
                    singleDocument.title,
                    'removeBookmark',
                  )
                  removeBookmark(singleDocument._id, {
                    onError: () => setBookmarkState(true),
                  })
                } else {
                  trackBookmarkButtonClick(singleDocument.title, 'addBookmark')
                  addBookmark(
                    { documentId: singleDocument._id },
                    {
                      onError: () => setBookmarkState(false),
                    },
                  )
                }
              }}
            >
              {bookmarkState ? <StyledBookmarkFilledIcon /> : <BookmarkIcon />}
            </BookmarkWrapper>
          </Tooltip>
        </DocumentButtons>
      </DocumentWrapper>
    </>
  )
}

function trackBookmarkButtonClick(
  title: string,
  type: 'removeBookmark' | 'addBookmark',
) {
  const tagManagerArgs = {
    dataLayer: {
      event: 'internal_click',
      click_type:
        type === 'removeBookmark'
          ? 'Remove Bookmark from Document'
          : 'Bookmark Document',
      click_subtype: title,
    },
  }
  TagManager.dataLayer(tagManagerArgs)
}

export function getFileExtension(documentName: string) {
  return documentName?.split('.').pop()?.toLowerCase() || ''
}
